import { useApiClient } from "@/modules/api/apiClient.store.ts";
import { checkForOmisePersistentData } from "@/modules/billing/omise/parseOmisePersistentData.ts";
import { getPaymentTarget, PaymentResult, type PaymentProps } from "@/modules/billing/payment/PaymentParams.ts";
import { creditBuyRequest, redirectToGatewayAndWaitForResponse } from "@/modules/billing/payment/paymentUtils.ts";
import { t_StartingPayment } from "@/modules/billing/progressLabels.tsx";
import { useUnmountSignal } from "@/utils/react/useUnmountSignal.tsx";
import type { OmiseCreditBuyBody, OmiseCreditCardConfig } from "@warrenio/api-spec/spec.oats.gen";
import { useImperativeHandle } from "react";
import invariant from "tiny-invariant";

export default function OmiseCardPaymentForm({ actionsRef }: { config: OmiseCreditCardConfig } & PaymentProps) {
    const apiClient = useApiClient();
    const signal = useUnmountSignal();

    useImperativeHandle(actionsRef, () => ({
        needsPopUp(_params) {
            return true;
        },
        async makePayment(params) {
            const { getCard, progress, account, popup, returnUrl } = params;

            progress(t_StartingPayment);
            const buyResult = await creditBuyRequest<OmiseCreditBuyBody>(apiClient, {
                billing_account_id: account.id,
                payment_object_id: getCard().id,
                ...getPaymentTarget(params),
                on_session: true,
                // XXX: The server might add a suffix to this URL, but we don't care about it
                front_base_url: returnUrl,
                force_3ds: true,
            });

            const {
                relay_props: { authorize_uri, data_ref },
            } = buyResult;
            invariant(data_ref, "data_ref");

            if (authorize_uri) {
                return await redirectToGatewayAndWaitForResponse({
                    popup,
                    progress,
                    redirectUrl: authorize_uri,
                    signal,
                    check: async () =>
                        await checkForOmisePersistentData({
                            apiClient,
                            account,
                            persistent_data_ref: data_ref,
                            signal,
                        }),
                });
            } else {
                // TODO: Do we still need to check for success via persistent data here?
                return PaymentResult.SUCCESS;
            }
        },
    }));

    return null;
}
